import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ButtonLink } from '@/components/ui/button-link';
import { Container } from '@/components/ui/container';
import { Logo } from '@/components/brand/logo';
import { cn, focusRing } from '@/lib/utils';

const NAV_LINKS = [
  { to: '/services', label: 'Solutions' },
  { to: '/philosophy', label: 'Philosophy' },
  { to: '/process', label: 'Process' },
  { to: '/insights', label: 'Insights' },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full',
        'bg-white/70 backdrop-blur-md',
        'border-b border-outline-variant/50 shadow-sm',
      )}
    >
      <Container size="xl" className="flex h-16 items-center justify-between gap-6">
        <Link
          to="/"
          aria-label="HumAIne home"
          className={cn('inline-flex items-center rounded-md', focusRing)}
        >
          <Logo />
        </Link>

        <nav aria-label="Primary" className="hidden md:block">
          <ul className="flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      'relative inline-flex h-10 items-center rounded-md px-3 text-sm font-medium transition-colors',
                      'text-on-surface-variant hover:text-primary hover:bg-surface-container',
                      focusRing,
                      isActive && [
                        'text-primary bg-surface-container',
                        'after:absolute after:bottom-0 after:inset-x-3 after:h-0.5 after:rounded-full after:bg-gradient-brand',
                      ],
                    )
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden md:block">
          <ButtonLink to="/contact" size="sm" data-testid="header-cta">
            Get an Outcome Plan
          </ButtonLink>
        </div>

        <Button
          variant="ghost"
          size="icon"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden"
        >
          {open ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <Menu className="size-5" aria-hidden="true" />
          )}
        </Button>
      </Container>

      {open ? (
        <div
          id="mobile-nav"
          className="border-t border-outline-variant/50 bg-white/90 backdrop-blur-md md:hidden"
        >
          <Container size="xl" className="py-4">
            <nav aria-label="Mobile">
              <ul className="flex flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <NavLink
                      to={link.to}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          'text-on-surface-variant hover:text-primary hover:bg-surface-container',
                          focusRing,
                          isActive && 'text-primary bg-surface-container',
                        )
                      }
                    >
                      {link.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-4">
              <ButtonLink to="/contact" size="md" className="w-full">
                Get an Outcome Plan
              </ButtonLink>
            </div>
          </Container>
        </div>
      ) : null}
    </header>
  );
}
